/**
 * 메일함 추가 · 이동 팝업 (2026-09-05).
 *
 * 한 벌이 두 일을 한다 — **고르는 컨트롤이 같기 때문**이다(상위 메일함 하나).
 * 두 벌로 만들면 3단 제한·자기 아래로 못 옮김 같은 규칙이 한쪽에만 붙는 날이 온다.
 *
 * ★★ **「상위 메일함」은 늘 보이고, 맨 위 항목이 「내 메일함」이다** (2026-09-06 사용자 지적).
 *    처음에는 체크박스로 열었는데, **새 계정은 만든 메일함이 하나도 없어** 체크를 켜도
 *    고를 것이 0개였다 — 「고르세요」만 뜨고 아무것도 못 골라 **메일함을 만들 수가 없었다.**
 *    맨 위를 「내 메일함」으로 세우면 목록이 비는 일이 없고, 왼쪽 레일의 나무와도 말이 같아진다
 *    (레일에서도 내가 만든 메일함은 **「내 메일함」 아래**에 선다).
 * ★★ **목록은 body 로 띄운다**(`portal`). 모달 본문이 `overflow-y:auto` 라 안 띄우면
 *    목록이 상자에 잘려 **스크롤만 생기고 고를 수가 없다** — 이것이 그 신고의 절반이었다.
 * ★ 서버가 주는 거절(같은 이름 · 3단 초과)은 **사람 말로 옮겨** 그 자리에 적는다.
 */

import { useMemo, useState } from 'react';
import { Button } from '../../components/shared/Button';
import { FormField } from '../../components/shared/FormField';
import { Input } from '../../components/shared/Input';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { Select } from '../../components/shared/Select';
import { tMail } from '../../lib/mail-i18n';
import type { MailFolderNode } from '../../api/mail';

/**
 * 「내 메일함」 = 맨 위. 서버에는 `parentId: null` 로 간다.
 * ★ 빈 문자열을 쓰지 않는다 — 셀렉트에서 「아직 안 고름」과 구별이 안 된다.
 */
export const MBOX_ROOT = '__root__';

export interface MailFolderModalProps {
  mode: 'create' | 'move';
  /** 옮기는 중이면 그 메일함. 만들 때는 null. */
  target?: MailFolderNode | null;
  /** 고를 수 있는 상위 후보(자기 자신·자기 자손은 부르는 쪽이 이미 뺐다). 「내 메일함」은 여기가 붙인다. */
  parents: Array<{ id: string; label: string }>;
  busy?: boolean;
  /** 서버가 거절한 이유 — 이미 사람 말로 옮겨진 것. */
  error?: string | null;
  onClose: () => void;
  onSubmit: (input: { name: string; parentId: string | null }) => void;
}

export function MailFolderModal(props: MailFolderModalProps) {
  const { mode, target, parents, busy, error, onClose, onSubmit } = props;
  const [name, setName] = useState(target?.name ?? '');
  const [parentId, setParentId] = useState<string>(target?.parentId ?? MBOX_ROOT);
  const [local, setLocal] = useState<string | null>(null);

  /** 맨 위는 늘 「내 메일함」이다 — 그래서 이 목록은 **비지 않는다.** */
  const options = useMemo(
    () => [
      { value: MBOX_ROOT, label: tMail('mail.folder.my') },
      ...parents.map((p) => ({ value: p.id, label: p.label })),
    ],
    [parents],
  );
  const title = mode === 'create' ? tMail('mail.mbox.add_title') : tMail('mail.mbox.move');

  const submit = () => {
    if (mode === 'create' && !name.trim()) { setLocal(tMail('mail.mbox.err_name')); return; }
    if (!parentId) { setLocal(tMail('mail.mbox.err_parent')); return; }
    setLocal(null);
    onSubmit({
      name: name.trim() || (target?.name ?? ''),
      parentId: parentId === MBOX_ROOT ? null : parentId,
    });
  };

  return (
    <Modal
      title={title}
      size="sm"
      onClose={onClose}
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={onClose} disabled={busy}>{tMail('mail.mbox.cancel')}</Button>
          <Button variant="primary" onClick={submit} loading={busy}>
            {mode === 'create' ? tMail('mail.mbox.create') : tMail('mail.mbox.move')}
          </Button>
        </ModalActions>
      )}
    >
      <div className="mboxmodal">
        {mode === 'create' && (
          <FormField label={tMail('mail.mbox.name_label')} required>
            <Input
              value={name}
              maxLength={100}
              autoFocus
              placeholder={tMail('mail.mbox.name_ph')}
              onChange={(e) => { setName(e.target.value); if (local) setLocal(null); }}
              /* ★ Enter 로 두 번 만들어지지 않게 — 폼이 아니라 이 한 줄에서 받는다. */
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (!busy) submit(); } }}
              fullWidth
            />
          </FormField>
        )}

        {/* ★ `ariaLabel` 을 주지 않는다 — `aria-label` 은 눈에 보이는 라벨을 «이겨서»,
            소리로는 「상위 메일함」 대신 자리표시자가 읽힌다(Combobox 주석 참조). */}
        <FormField label={tMail('mail.mbox.parent_label')} required hint={tMail('mail.mbox.parent_hint')}>
          <Select
            value={parentId}
            onChange={(v) => { setParentId(v); if (local) setLocal(null); }}
            options={options}
            placeholder={tMail('mail.mbox.parent_ph')}
            searchable={options.length > 8}
            /* ★ 모달 본문이 `overflow-y:auto` 라 — 안 띄우면 목록이 잘려 고를 수가 없다. */
            portal
          />
        </FormField>

        {(local ?? error) && (
          <p className="mboxmodal__err" role="alert">{local ?? error}</p>
        )}
      </div>
    </Modal>
  );
}
